import { ThemeProvider } from "@emotion/react";
import { Box, CircularProgress, CssBaseline } from "@mui/material";
import { signInWithCustomToken } from "firebase/auth";
import {
    createContext,
    lazy,
    useEffect,
    useMemo,
    useState,
} from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";

import { RoutePageLifecycle } from "./AppRouteLifecycle";
import PermissionProtectedRoute from "./components/auth/PermissionProtectedRoute";
import SimpleProtectedRoute from "./components/auth/SimpleProtectedRoute";
import { DevLogDownloadButton } from "./components/debug/DevLogDownloadButton";
import { PreviewEmailOverrideGate } from "./components/previewEmail/PreviewEmailOverrideGate";
import { useAuthState } from "./HelloCashflowDomain/Authentication/hooks/useAuthState";
import Login from "./pages/Login";
import { createAdminTheme } from "./style/adminTheme";

import memoizedAuth from "@/lib/firebase/MemoizedAuth";
import { ADMIN_ROUTES, isAdminV2Route } from "@/routing/adminRoutes";
import { ErrorBoundary } from "@/utils/errorHandling";
import { isAdminV2FoundationsEnabled } from "@/utils/featureFlags";
import { sanitizeAdminRouteForLogging } from "@/utils/logging/sanitizeAdminRouteForLogging";
import {
    CORRELATION_ID_HEADER,
    generateCorrelationId,
} from "@/utils/logging/correlationId";

const logger = FrontendLoggerFactory.getLogger().withComponent("App");
const SharedDocViewer = lazy(() => import("@/pages/public/SharedDocViewer"));

const LoadingFallback = () => (
    <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='200px'>
        <CircularProgress />
    </Box>
);

const createGlobalErrorHandlers = () => {
    const routeFromLocation = () =>
        sanitizeAdminRouteForLogging(window.location.pathname);

    const handleWindowError = (event: ErrorEvent) => {
        const unhandledError =
            event.error instanceof Error
                ? event.error
                : new Error(event.message || "Unhandled window error");
        FrontendLoggerFactory.getLogger()
            .withComponent("App")
            .withCorrelationId(generateCorrelationId())
            .error(
                "UNHANDLED_ERROR: Global window onerror handler",
                unhandledError,
                {
                    feature: "FND",
                    operation: "UNHANDLED_ERROR",
                    route: routeFromLocation(),
                },
            );
    };

    const handleWindowRejection = (event: PromiseRejectionEvent) => {
        const reason = event.reason;
        const reasonError =
            reason instanceof Error
                ? reason
                : new Error(typeof reason === "string" ? reason : "Unhandled rejection");

        FrontendLoggerFactory.getLogger()
            .withComponent("App")
            .withCorrelationId(generateCorrelationId())
            .error(
                "UNHANDLED_REJECTION: Global window promise rejection handler",
                reasonError,
                {
                    feature: "FND",
                    operation: "UNHANDLED_REJECTION",
                    route: routeFromLocation(),
                },
            );
    };

    return {
        handleWindowError,
        handleWindowRejection,
    };
};

function App() {
    // Use the proven working pattern - no AuthProvider needed!
    const { user, loading } = useAuthState();
    const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);
    const [autoLoginInProgress, setAutoLoginInProgress] = useState(false);
    const adminV2FoundationsEnabled = isAdminV2FoundationsEnabled();

    useEffect(() => {
        const { handleWindowError, handleWindowRejection } =
            createGlobalErrorHandlers();

        window.addEventListener("error", handleWindowError);
        window.addEventListener("unhandledrejection", handleWindowRejection);

        return () => {
            window.removeEventListener("error", handleWindowError);
            window.removeEventListener(
                "unhandledrejection",
                handleWindowRejection,
            );
        };
    }, []);

    // Auto-login for testing purposes
    useEffect(() => {
        // Avoid racing token-based login (`/login?token=...`) with the localhost auto-login.
        // The login page owns that flow and will sign out/sign in deterministically.
        const params = new URLSearchParams(window.location.search);
        if (params.get("token")) {
            return;
        }

        const testUserId = import.meta.env
            .VITE_LOCALHOST_TEST_USER_FIREBASE_UUID;

        if (testUserId && !user && !loading && !autoLoginAttempted) {
            setAutoLoginAttempted(true);
            setAutoLoginInProgress(true);
            const correlationId = generateCorrelationId();
            const requestLogger = logger.withCorrelationId(correlationId);

            // Development auto-login using localhost-only endpoint
            requestLogger.debug(
                "Auto-login requesting development custom token",
                { operation: "auto_login_request_token" },
            );
            fetch(
                `${
                    import.meta.env.VITE_API_URL
                }/dev/custom-token?uid=${testUserId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        [CORRELATION_ID_HEADER]: correlationId,
                    },
                },
            )
                .then(response => {
                    if (!response.ok) {
                        // Log the specific error for debugging
                        return response
                            .json()
                            .then(errorData => {
                                const errorMessage =
                                    typeof errorData?.error === "string" &&
                                    errorData.error.trim().length > 0
                                        ? errorData.error
                                        : response.statusText;

                                throw new Error(
                                    `HTTP ${response.status}: ${errorMessage}`,
                                );
                            })
                            .catch(() => {
                                // Fallback if response isn't JSON
                                throw new Error(
                                    `HTTP ${response.status}: ${response.statusText}`,
                                );
                            });
                    }
                    return response.json();
                })
                .then(data => {
                    if (data.customToken) {
                        const auth = memoizedAuth.getAuth();
                        requestLogger.debug(
                            "Auto-login successful (development custom token)",
                            { operation: "auto_login_success" },
                        );
                        return signInWithCustomToken(auth, data.customToken);
                    }
                    throw new Error("No custom token received in response");
                })
                .catch(error => {
                    requestLogger.error(
                        "Auto-login failed; falling back to manual login",
                        error instanceof Error ? error : undefined,
                        { operation: "auto_login_failed" },
                    );
                })
                .finally(() => {
                    setAutoLoginInProgress(false);
                });
        }
    }, [user, loading, autoLoginAttempted]);

    // CRITICAL: Block ALL rendering until auth state is determined
    // This is the key that prevents race conditions
    if (loading || autoLoginInProgress) {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "100vh",
                }}>
                <CircularProgress />
            </div>
        );
    }

    const visibleAdminRoutes = ADMIN_ROUTES.filter(route => {
        if (adminV2FoundationsEnabled) {
            return true;
        }

        return !isAdminV2Route(route);
    });

    // Now safe to render - auth state is known
    return (
        <ErrorBoundary componentName="AppErrorBoundary">
            <Routes>
                <Route
                    path='/login'
                    element={
                        <RoutePageLifecycle
                            route={{ id: "login", path: "/login" }}
                            fallback={<LoadingFallback />}
                        >
                            <Login />
                        </RoutePageLifecycle>
                    }
                />
                <Route
                    path='/share/docs/:token'
                    element={
                        <RoutePageLifecycle
                            route={{ id: "shared_docs", path: "/share/docs/:token" }}
                            fallback={<LoadingFallback />}
                        >
                            <SharedDocViewer />
                        </RoutePageLifecycle>
                    }
                />
                {visibleAdminRoutes.map(route => (
                    <Route
                        key={route.id}
                        path={route.path}
                        element={
                            <SimpleProtectedRoute requiredClaims={["admin"]}>
                                <PermissionProtectedRoute
                                    requiredPermission={route.requiredPermission}
                                    requiredMenuVisibility={route.requiredMenuVisibility}
                                >
                                    <RoutePageLifecycle
                                        route={route}
                                        fallback={<LoadingFallback />}
                                    >
                                        <route.Component />
                                    </RoutePageLifecycle>
                                </PermissionProtectedRoute>
                            </SimpleProtectedRoute>
                        }
                    />
                ))}
                <Route path='*' element={<Navigate to='/' replace />} />
            </Routes>
        </ErrorBoundary>
    );
}

export const ColorModeContext = createContext({
    toggleColorMode: () => {},
    mode: "" as "light" | "dark",
});

/**
 * Determines the initial admin theme mode.
 *
 * @remarks
 * Persisted mode wins; otherwise fall back to the OS preference.
 */
const getInitialMode = (): "light" | "dark" => {
    const storedMode = localStorage.getItem("admin-theme-mode");
    if (storedMode === "light" || storedMode === "dark") {
        return storedMode;
    }
    return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
};

/**
 * Top-level app shell for HellocashflowAdmin.
 *
 * @remarks
 * Owns theme selection/persistence and renders the route tree via `App()`.
 */
export default function AdminApp() {
    const [mode, setMode] = useState<"light" | "dark">(getInitialMode());

    const toggleColorMode = useMemo(
        () => () => {
            setMode(prevMode => (prevMode === "light" ? "dark" : "light"));
        },
        [],
    );

    useEffect(() => {
        localStorage.setItem("admin-theme-mode", mode);
        if (typeof document !== "undefined") {
            document.documentElement.setAttribute(
                "data-admin-color-scheme",
                mode,
            );
        }
    }, [mode]);

    const theme = useMemo(() => createAdminTheme(mode), [mode]);

    // No AuthProvider needed! Firebase is initialized early in index.tsx
    return (
        <ColorModeContext.Provider value={{ toggleColorMode, mode }}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <DevLogDownloadButton />
                <PreviewEmailOverrideGate />
                <App />
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
}
